import { ColDef, GridApi, GridOptions, INumberFilterParams, ISetFilterParams, ProcessCellForExportParams } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_FR, AG_GRID_LOCALE_IT } from '@ag-grid-community/locale';

/**
 * Default ColDef for read-only data grids: sortable, resizable, filterable, sensible minWidth.
 */
export const READ_ONLY_DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
  minWidth: 100,
};

/**
 * Filter params for read-only numeric columns. The Apply button defers filtering until the
 * user confirms (click Apply or press Enter), so a partial value never filters mid-typing;
 * Clear resets the single condition. Shared so every measure grid filters identically.
 */
export const ENTER_APPLY_NUMBER_FILTER_PARAMS: INumberFilterParams = {
  maxNumConditions: 1,
  buttons: ['clear', 'apply'],
  closeOnApply: true,
};

/**
 * Filter params for every set filter. Without Excel mode, "(Select All)" only toggles the
 * search hits, so after a search, "deselect all" then "pick the hit" re-selects every value
 * and clears the filter (CES-978). In Excel mode the search hits replace the selection
 * as the user types, and the panel offers Reset instead of Select All on the hits.
 */
export const EXCEL_SET_FILTER_PARAMS: ISetFilterParams = { excelMode: 'mac' };

/**
 * Drives the no-rows overlay from the displayed row count, so an empty result (filter match or
 * empty period) reads as a message instead of a blank grid, while the grid (and its filters)
 * stays mounted so a clear-filters action can always recover it. Deferred because the client
 * row model reports the post-filter count one tick after ``onModelUpdated`` fires; harmless for
 * SSRM. Safe to wire directly as ``onModelUpdated: e => syncNoRowsOverlay(e.api)``.
 */
export function syncNoRowsOverlay(api: GridApi): void {
  setTimeout(() => {
    if (api.isDestroyed()) return;
    if (api.getDisplayedRowCount() === 0) api.showNoRowsOverlay();
    else api.hideOverlay();
  });
}

/**
 * AG-Grid no-rows overlay markup shared by the data grids (info icon + localized text).
 */
export const noRowsOverlay = (text: string): string =>
  `<span class="ag-overlay-no-rows-center"><i class="pi pi-info-circle" style="margin-right:0.5rem;"></i>${text}</span>`;

/**
 * Clipboard cell hook shared by the copyable read-only grids: empty cells copy as empty
 * strings (the screen shows '-'), everything else keeps its display format.
 */
export function formatCellForClipboard(params: ProcessCellForExportParams): string {
  if (params.value === null || params.value === undefined) return '';
  return params.formatValue(params.value);
}

/** Total cell count across all selected cell ranges. */
export function countSelectedCells(api: GridApi): number {
  const ranges = api.getCellRanges();
  if (!ranges?.length) return 0;
  let count = 0;
  for (const range of ranges) {
    const start = Math.min(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
    const end = Math.max(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
    count += (end - start + 1) * range.columns.length;
  }
  return count;
}

/**
 * GridOptions preset for time-series tables: timestamp-based row id, no row animation.
 */
export const timeSeriesGridOptions = <T extends { timestamp: Date | string }>(): GridOptions<T> => ({
  theme: 'legacy',
  animateRows: false,
  getRowId: params => {
    const ts = params.data.timestamp;
    return ts instanceof Date ? ts.toISOString() : String(ts);
  },
});

/** Libellés ag-grid dans la langue courante, l'anglais faisant office de repli.
 *
 * La grille lit ses libellés une seule fois, à sa création : un changement de langue
 * en cours de session ne les retraduit pas, exactement comme les en-têtes de colonnes. */
export function agGridLocale(lang: string): Record<string, string> {
  switch (lang.slice(0, 2).toLowerCase()) {
    case 'fr':
      return AG_GRID_LOCALE_FR;
    case 'de':
      return AG_GRID_LOCALE_DE;
    case 'it':
      return AG_GRID_LOCALE_IT;
    default:
      return AG_GRID_LOCALE_EN;
  }
}
